import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Camera, User, Loader2 } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

interface ProfileImageUploadProps {
  avatarUrl?: string | null;
  firstName?: string | null;
  userRole?: string;
}

export const ProfileImageUpload = ({ avatarUrl, firstName, userRole }: ProfileImageUploadProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const isCompanyLogo = userRole === 'shipper' || userRole === 'transporter';
  const label = isCompanyLogo 
    ? t('settings.profile.companyLogo') 
    : t('settings.profile.profilePhoto');

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('settings.profile.imageTypeError'));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('settings.profile.imageSizeError'));
      return;
    }

    setIsUploading(true);

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      // Delete old avatar if exists
      if (avatarUrl) {
        const oldPath = avatarUrl.split('/avatars/')[1];
        if (oldPath) {
          await supabase.storage.from('avatars').remove([oldPath]);
        }
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update user profile with new avatar URL
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast.success(t('settings.profile.imageUploadSuccess'));
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(t('settings.profile.imageUploadError'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 mb-6 pb-6 border-b border-border">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      
      <div className="relative">
        <Avatar className="h-24 w-24 border-2 border-border">
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} alt="Profile" className="object-cover" />
          ) : (
            <AvatarFallback className="bg-secondary text-secondary-foreground text-2xl">
              {firstName?.charAt(0).toUpperCase() || <User className="h-10 w-10" />}
            </AvatarFallback>
          )}
        </Avatar>
        
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileSelect}
        disabled={isUploading}
      />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="gap-2"
      >
        <Camera className="h-4 w-4" />
        {t('settings.profile.changeImage')}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        {t('settings.profile.imageHint')}
      </p>
    </div>
  );
};
